import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type {
    EmailSuppressionReason,
    SuppressionEntry,
} from '@/types/communications'
import {
    Badge,
    Card,
    CardContent,
    ConfirmDialog,
    EmptyState,
    Field,
    Input,
    LoadingPanel,
    Modal,
    PillButton,
    SectionTitle,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './_shared'
import { ShieldX, X, Plus, AlertCircle } from 'lucide-react'

const REASONS: {
    value: EmailSuppressionReason
    label: string
    variant: 'default' | 'success' | 'warning' | 'error' | 'info'
}[] = [
    { value: 'USER_UNSUBSCRIBED', label: 'User unsubscribed', variant: 'default' },
    { value: 'HARD_BOUNCE', label: 'Hard bounce', variant: 'error' },
    { value: 'COMPLAINT', label: 'Spam complaint', variant: 'warning' },
    { value: 'MANUAL', label: 'Added manually', variant: 'warning' },
]

export function SuppressionsTab() {
    const [items, setItems] = useState<SuppressionEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [reasonFilter, setReasonFilter] = useState('')
    const [showAdd, setShowAdd] = useState(false)
    const [addEmail, setAddEmail] = useState('')
    const [addReason, setAddReason] = useState<EmailSuppressionReason>('MANUAL')
    const [busy, setBusy] = useState(false)
    const [confirmRemove, setConfirmRemove] = useState<string | null>(null)

    const load = async () => {
        setLoading(true)
        try {
            const res = await adminApi.getSuppressions({
                reason: reasonFilter || undefined,
                limit: 200,
            })
            setItems(res.items)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reasonFilter])

    const handleAdd = async () => {
        if (!addEmail.includes('@')) return
        setBusy(true)
        try {
            await adminApi.addSuppression({ email: addEmail.trim(), reason: addReason })
            setAddEmail('')
            setShowAdd(false)
            await load()
        } finally {
            setBusy(false)
        }
    }

    const handleRemove = async () => {
        if (!confirmRemove) return
        setBusy(true)
        try {
            await adminApi.removeSuppression(confirmRemove)
            await load()
        } finally {
            setBusy(false)
            setConfirmRemove(null)
        }
    }

    return (
        <div className="space-y-6">
            <SectionTitle
                title="Suppressions"
                description="Email addresses Libra will never send to. Populated by unsubscribes, hard bounces, spam complaints, or manual additions."
                actions={
                    <PillButton variant="primary" onClick={() => setShowAdd(true)}>
                        <Plus className="h-3.5 w-3.5" /> Add suppression
                    </PillButton>
                }
            />

            <div className="flex items-center gap-3">
                <select
                    value={reasonFilter}
                    onChange={e => setReasonFilter(e.target.value)}
                    className="h-9 min-w-[180px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                    <option value="">All reasons</option>
                    {REASONS.map(r => (
                        <option key={r.value} value={r.value}>
                            {r.label}
                        </option>
                    ))}
                </select>
                <span className="text-sm text-gray-500">{items.length} addresses</span>
            </div>

            {loading ? (
                <LoadingPanel label="Loading suppressions…" />
            ) : items.length === 0 ? (
                <EmptyState
                    icon={ShieldX}
                    title="No suppressed emails"
                    description="Addresses land here when users unsubscribe, hard-bounce, or report spam."
                />
            ) : (
                <Card>
                    <CardContent className="!p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Reason</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead>Added</TableHead>
                                    <TableHead className="w-12"> </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map(row => {
                                    const cfg = REASONS.find(r => r.value === row.reason)
                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell className="font-medium">
                                                {row.email}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={cfg?.variant ?? 'default'}>
                                                    {cfg?.label ?? row.reason}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {row.user ? (
                                                    <div className="text-xs">
                                                        <div className="text-gray-900">
                                                            {row.user.name || row.user.email}
                                                        </div>
                                                        <code className="font-mono text-gray-500">
                                                            {row.user.id}
                                                        </code>
                                                    </div>
                                                ) : (
                                                    <span className="italic text-gray-400">
                                                        External
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs tabular-nums text-gray-600">
                                                {new Date(row.createdAt).toLocaleString(
                                                    undefined,
                                                    {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                    }
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <button
                                                    onClick={() =>
                                                        setConfirmRemove(row.email)
                                                    }
                                                    title="Remove from suppression list"
                                                    className="rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            <div className="flex items-start gap-2 text-xs text-gray-500">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span>
                    Removing an address re-enables sends. The user's
                    <code className="mx-1 rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">
                        unsubscribedAt
                    </code>
                    preference flag stays set until they re-subscribe in settings.
                </span>
            </div>

            <Modal
                open={showAdd}
                onClose={() => setShowAdd(false)}
                title="Add suppression"
                description="Block this email from receiving any future sends."
                footer={
                    <>
                        <PillButton onClick={() => setShowAdd(false)} disabled={busy}>
                            Cancel
                        </PillButton>
                        <PillButton
                            variant="primary"
                            onClick={handleAdd}
                            disabled={busy || !addEmail.includes('@')}
                        >
                            {busy ? 'Suppressing…' : 'Suppress'}
                        </PillButton>
                    </>
                }
            >
                <div className="space-y-4">
                    <Field label="Email" required>
                        <Input
                            type="email"
                            value={addEmail}
                            onChange={e => setAddEmail(e.target.value)}
                            placeholder="someone@example.com"
                        />
                    </Field>
                    <Field label="Reason">
                        <Select
                            value={addReason}
                            onChange={e =>
                                setAddReason(e.target.value as EmailSuppressionReason)
                            }
                            options={REASONS.map(r => ({ value: r.value, label: r.label }))}
                        />
                    </Field>
                </div>
            </Modal>

            <ConfirmDialog
                open={confirmRemove !== null}
                onCancel={() => setConfirmRemove(null)}
                onConfirm={handleRemove}
                title="Remove from suppression list?"
                message={`${confirmRemove} will start receiving emails again on the next applicable send.`}
                confirmLabel="Remove"
                confirmVariant="danger"
                loading={busy}
            />
        </div>
    )
}
